<template>
  <div id="directory" class="grid-container">

    <div class="vue-search">
      <label class="accessible"
      for="search-bar">
        Filter results by title, keyword, or acronym
      </label>
      <input
        id="search-bar"
        v-model="search"
        class="search-field"
        type="text"
        :placeholder="$t('Filter results')"
      />
      <button v-if="search" class="clear-search-btn" @click="clearSearch">
        <i class="fas fa-times" />
      </button>
      <button
        class="search-submit"
        @click="requestData()"
      >
        <i class="fa-solid fa-magnifying-glass" />
      </button>
    </div>
    <div v-if="loading" class="loading-icon">
        <i class="fas fa-spinner fa-spin fa-3x loadingdir" />
      </div>
    <div class="filter-summary">
      <span class="filter-text" v-if="filteredDepartments.length > 0">
        Showing {{ filteredDepartments.length }} results
        <span v-if="search.length > 0">
          for <strong><em>"{{ search }}"</em></strong>
        </span>
      </span>
      <span class="filter-text" v-else-if="!loading">
        No results found
        <span v-if="search.length > 0">
          for <strong><em>"{{ search }}"</em></strong>
        </span>
      </span>
      <input
        v-if="search.length > 0"
        type="submit"
        class="clear-button"
        value="Clear all"
        @click="clearSearch()"
      >
    </div>
    <div class="hint-text" v-if="filteredDepartments.length === 0 && !loading">
      <strong>There are no matching results.</strong>
      <br>
      <br>
      Improve your search results by:
      <br>
      <br>
      <ul>
        <li>Using different or fewer search terms.</li>
        <li>Checking your spelling.</li>
        <li>Removing or adjusting any filters.</li>
      </ul>
      Want to start over? Select “Clear all” to reset the search settings.
    </div>
    <table 
    v-if="!loading && filteredDepartments.length > 0"
    class="stack theme-light archive-results">
      <thead
        class="center bg-white hide-for-small-only"
        data-top-anchor="filter-results:bottom"
        data-btm-anchor="page:bottom"
        data-options="marginTop:4.8;"
      >
        <th id="name">
            <span>{{ $t('Name') }}</span>
          </th>
          <th id="description">
            <span>{{ $t('Description') }}</span>
          </th>
          <th id="connect">{{ $t('Connect') }}</th>
        </thead>
      <tbody>
        <tr v-for="department in filteredDepartments" :key="department.name">
          <td>
            <a :href="translateLink(department.permalink)" v-html="department.name"></a>
          </td>
          <td>{{ department.description }}</td>
          <td>
            <div class="grid-x mbm mtm" v-if="department.email">
              <div class="cell small-3">
                <i class="fas fa-envelope fa-lg" />
              </div>
              <div class="cell auto">
                <a :href="'mailto:' + department.email">{{ department.email }}</a>
              </div>
            </div>
            <div class="grid-x mbm mtm" v-if="department.phone">
              <div class="cell small-3">
                <i class="fa fa-phone fa-lg phone-icon" />
              </div>
              <div class="cell auto">
                <a
                  v-if="department.phone !== '311'"
                  :href="'tel:' + department.phone['area'] + department.phone['co-code'] + department.phone['subscriber-number']"
                >({{department.phone['area']}}) {{ department.phone['co-code'] }}-{{ department.phone['subscriber-number'] }}</a>
                <span class="helper-text" v-html="department.phone['helper-text']" />
                <a
                  v-if="department.phone == '311'"
                  :href="'tel:' + department.phone"
                >{{ department.phone }}</a>

                <div v-if="department.phone_multi">
                  <div v-for="phone in department.phone_multi" :key="phone['subscriber-number']">
                    <a
                      :href="'tel:' + phone['area'] + phone['co-code'] + phone['subscriber-number']"
                    >({{phone['area']}}) {{ phone['co-code'] }}-{{ phone['subscriber-number'] }}</a>
                    <span class="helper-text" v-html="phone['helper-text']" />
                  </div>
                </div>
                <div v-if="department.fax">Fax: {{ department.fax }}</div>
                <div v-if="department.tty">TTY: {{ department.tty }}</div>
              </div>
            </div>
            <div
              class="grid-x mbm mtm"
              v-if="department.instagram || department.facebook || department.twitter"
            >
              <div class="cell small-3">
                <i class="fas fa-at fa-lg" />
              </div>
              
              <div v-if="department.instagram" class="cell small-4">
                <a :href="department.instagram">
                  <div class="accessible">instagram link</div>
                  <i class="fab fa-instagram fa-lg" />
                </a>
              </div>
              <div v-if="department.facebook" class="cell small-4">
                <a :href="department.facebook">
                  <div class="accessible">facebook link</div>
                  <i class="fab fa-facebook fa-lg" />
                </a>
              </div>
              <div v-if="department.twitter" class="cell small-4 ">
               
                <a :href="department.twitter">
                   <div class="accessible">twitter link</div>
                  <i class="fab fa-x-twitter fa-lg" />
                </a>
              </div>
              
            </div>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>

<script>
import axios from "axios";
import Vue from "vue";
import VueFuse from "vue-fuse";
import { loadLanguageAsync } from './../i18n.js';

Vue.use(VueFuse);
// const slug = "https://localhost:19107/wp-json/departments/v1/list";
// const slug = "https://staging-admin.phila.gov/wp-json/departments/v1/list";
const slug = "https://api.phila.gov/phila/department/list";

export default {
  name: "Directory",
  components: {},
  props: {},
  filters: {},
  watch: {
    search() {
      this.filterDepartments();
    }
  },
  mounted() {
    this.loading = true;
    this.getDeptDetaiils();
    loadLanguageAsync(this.language);

  },
  computed: {
    currentRouteName() {
      return this.isTranslated(window.location.pathname);
    },
    language() {
      let lang = this.isTranslated(window.location.pathname);
      const validLanguages = [ '/es', '/zh', '/ar', '/ht', '/fr', '/sw', '/pt', '/ru', '/vi' ];
      if (validLanguages.includes(lang)) {
        return lang.substring(1); 
      } 
      return 'en'; 
    },

    langSlug() {
      let vm = this;
      if (vm.language == 'en') {
        return slug;
      }
      const languageCode = vm.language; 
      const url = process.env.VUE_APP_BUCKET_URL + `${languageCode}/phila_department_list.json`;  
      return url;
    },
  },
  methods: {
    clearSearch(){
      this.search = '';
    },
    isTranslated(path) {
      let splitPath = path.split("/");
      const langList = [ 'zh', 'es','ar', 'fr', 'ru', 'ms', 'hi', 'pt', 'bn', 'id', 'sw', 'ja', 'de', 'ko', 'it', 'fa', 'tr', 'nl', 'te', 'vi', 'ht' ];
      for (let i = 0; i < splitPath.length; i++) {
        if (langList.indexOf(splitPath[i]) > -1) {
          return '/'+splitPath[i];
        }
      }
      return null;
    },
    translateLink(link) {
      let self = this;
      return self.currentRouteName ? self.currentRouteName+link : link;
    },
    filterDepartments() {
      if(this.search) {
        this.$search(this.search, this.departments, this.searchOptions).then(
          departments => {
            this.filteredDepartments = departments;
          }
        );
      } else {
        this.filteredDepartments = this.departments;
      }
    },
    async getDeptDetaiils() {
      return axios
        .get(this.langSlug)
        .then(async (response) => {
          this.departments = response.data;
          // this.filteredDepartments = this.departments;
          this.departments = this.departments.filter((v,i,a)=>a.findIndex(t=>(t.name === v.name))===i);
          this.departments = this.departments.sort((function(a, b) {
            var x = a['name']; var y = b['name'];
            return ((x < y) ? -1 : ((x > y) ? 1 : 0));
        }))

        this.filteredDepartments = this.departments;

          this.loading = false;
          return true;
        })
        .catch(() => {
          return false;
        });
    },
  },
  data: function () {
    return {
      departments: [],
      filteredDepartments: [],
      search: "",
      loading: false,
      searchOptions: {
        threshold: 0.3,
        tokenize: true,
        keys: ["name",
        "acronym",
        "email",
        "description" ]
      },
    };
  },
};
</script>

<style lang='scss' scoped>
#directory {
  table.theme-light th {
    text-transform: none;
  }
  table.theme-light thead {
    border-bottom: 3px solid #444;
  }
  .cell:last-child:not(:first-child) {
    float: left;
  }
  a {
    color: #0f4d90;
  }
  #name {
    width: 30%;
  }
  #description {
    width: 35%;
  }
  #connect {
    width: 35%;
  }

  .phone-icon {
    top: calc(50% - 13px);
    position: relative;
  }
  
  .vue-search {
    position: relative;
    display: flex;

    .search-field{
      min-height: 3.8rem;
      border: 2px solid #0f4d90;
      background: white;
    }

    .clear-search-btn {
      position: absolute;
      top:16px;
      right: 70px;
      padding: 0;
      font-size: 20px;
      background-color: #fff;
      opacity: 0.8;
      cursor: pointer;
      color: rgba(60, 60, 60, 0.5);
      &:hover {
      background: transparent;
      color: black;
    }
    }

    .search-submit{ 
      padding: 0.4rem;
      font-size: 2rem;
      font-weight: 400;
      background: #0f4d90;
      color: white;
      width: 3.8rem;
      height: 3.8rem;
      cursor: pointer;
    }

    .fa-magnifying-glass{
      font-weight: normal;
    }
  }
  span.filter-text {
    margin-right: 8px;
  }
  .clear-button{
    margin-left: 8px;
    border: none;
    background-color: transparent;
    color: #0f4d90;
    cursor: pointer;
    font-weight: 700;
    text-decoration: underline;
    padding: 0px;
  }
  .hint-text{
    background: rgba(150,201,255,.3);
    padding: 32px;
    margin-top: 2rem;
    width: fit-content;
  }
  .loading-icon {
    margin-top: 40px;
    text-align: center;
  }
  .helper-text {
    padding-left: 3px;
  }
}
</style>